import { useState, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Check, Loader2, RefreshCw } from 'lucide-react'
import type { Transaction, TransactionFilter } from './types'
import { useCommitMatches, useRunAutoMatch, useAutoMatchJob } from './hooks/useReconApi'
import { CommitDialog } from './CommitDialog'

interface ActionStripProps {
  transactions: Transaction[]
  clientId: string
  statementId: string
  bankAccountId: number
  activeFilter: TransactionFilter | null
  onFilterChange: (filter: TransactionFilter | null) => void
  hasSelectedConfirmed: boolean
}

export function ActionStrip({
  transactions,
  clientId,
  statementId,
  bankAccountId,
  activeFilter,
  onFilterChange,
  hasSelectedConfirmed,
}: ActionStripProps) {
  const commitMutation = useCommitMatches()
  const autoMatchMutation = useRunAutoMatch()
  const storageKey = `auto-match-job:${statementId}`
  const [activeJobId, setActiveJobId] = useState<string | null>(
    () => sessionStorage.getItem(storageKey)
  )
  const { data: jobData } = useAutoMatchJob(activeJobId)

  const autoConfirmed = transactions.filter(
    (t) => t.match?.state === 'auto_confirmed'
  ).length

  const noMatch = transactions.filter(
    (t) => !t.match || t.match.state === 'unmatched' || t.match.state === 'reversed'
  ).length

  const committed = transactions.filter(
    (t) => t.match?.state === 'committed'
  ).length

  const allConfirmed = transactions.filter((t) => t.match?.state === 'confirmed')

  const [commitDialogOpen, setCommitDialogOpen] = useState(false)

  // Hide global "Commit all reviewed" while user is operating on a selection of confirmed rows;
  // the SelectionBar shows "Commit N selected" in that mode so there's only one Commit button.
  const showGlobalCommit = allConfirmed.length > 0 && !hasSelectedConfirmed

  const handleCommitConfirm = (overrides: { doc_nom: string; reg_date: string }) => {
    const ids = allConfirmed.map((t) => t.match!.id)
    commitMutation.mutate(
      { match_ids: ids, client_id: clientId, ...overrides },
      { onSuccess: () => setCommitDialogOpen(false) },
    )
  }

  const isAutoMatching = !!activeJobId && jobData?.status === 'running'

  useEffect(() => {
    if (jobData && jobData.status !== 'running') {
      setActiveJobId(null)
      sessionStorage.removeItem(storageKey)
    }
  }, [jobData, storageKey])

  const handleAutoMatch = () => {
    autoMatchMutation.mutate(
      { client_id: clientId, statement_id: statementId, bank_account_id: bankAccountId },
      {
        onSuccess: (data) => {
          if (data.job_id && data.job_id !== '00000000-0000-0000-0000-000000000000') {
            setActiveJobId(data.job_id)
            sessionStorage.setItem(storageKey, data.job_id)
          }
        },
      }
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-neutral-100 px-6 py-4">
      {/* Row 1: filter pills (left) + paired global actions (right) */}
      <div className="flex items-center gap-3">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          {([
            { key: 'no_match', color: 'bg-red-500', label: 'No Match', count: noMatch },
            { key: 'auto_confirmed', color: 'bg-gray-400', label: 'Auto-Confirmed', count: autoConfirmed },
            { key: 'confirmed', color: 'bg-green-500', label: 'Confirmed', count: allConfirmed.length },
            { key: 'committed', color: 'bg-blue-500', label: 'Committed', count: committed },
          ] as const).map(({ key, color, label, count }) => (
            <Badge
              key={key}
              variant="secondary"
              className={`gap-1.5 pl-1 cursor-pointer transition-opacity ${activeFilter && activeFilter !== key ? 'opacity-40' : ''
                } ${activeFilter === key ? 'ring-2 ring-offset-1 ring-neutral-400' : ''}`}
              onClick={() => onFilterChange(activeFilter === key ? null : key)}
            >
              <span
                className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold text-white ${color}`}
              >
                {count}
              </span>
              {label}
            </Badge>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={autoMatchMutation.isLoading || isAutoMatching}
            onClick={handleAutoMatch}
          >
            {isAutoMatching ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {isAutoMatching && jobData
              ? `Matching ${jobData.processed_count}/${jobData.total_transactions} (${jobData.matched_count} matched)`
              : autoMatchMutation.isLoading
                ? 'Starting...'
                : 'Run auto-match'}
          </Button>
          {showGlobalCommit && (
            <Button
              size="sm"
              variant="blue"
              className="gap-1.5"
              disabled={commitMutation.isLoading}
              onClick={() => setCommitDialogOpen(true)}
            >
              <Check className="h-3.5 w-3.5" />
              {commitMutation.isLoading
                ? 'Committing...'
                : `Commit ${allConfirmed.length} reviewed`}
            </Button>
          )}
          <CommitDialog
            open={commitDialogOpen}
            onOpenChange={setCommitDialogOpen}
            transactions={allConfirmed}
            clientId={clientId}
            loading={commitMutation.isLoading}
            triggerLabel={`Commit ${allConfirmed.length}`}
            onConfirm={handleCommitConfirm}
          />
        </div>
      </div>

    </div>
  )
}
